// istanbul ignore file
import React, { useEffect, useState } from 'react'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import { type Stage, type Parameter } from './types'
import { FormHelperText, Grid, TextField, Typography } from '@mui/material'
import LanguageStrings from '../../../../i18n/locales'
import { fetchKpiList } from '../../redux/actionCreators'
import { type ParameterConfigState } from '../../redux/deviceParameterSlice'
import { type ConnectedProps, connect } from 'react-redux'
import { type RootState } from '../../../../store'
import { type KpiListState } from '../../redux/kpiListSlice'
import { fetchParameterConfigs, type ParameterConfig } from '../../services/deviceParameterService'
import { getDeviceLevelKpiList } from '../../services/deviceKpiDetailsService'
import { fetchKpiConfigs } from '../../services/deviceKpiParameterService'

const customKpi = LanguageStrings().ThermalIQStrings.CreateCustomKpiComponent
const commonTextStyle = { fontSize: '10px', display: 'block', color: '#F0F0F0', fontFamily: 'Honeywell Sans Web' }

interface ParameterFormProps {
  assetList: any
  index: number
  isTotalizer: boolean
  parameter: Parameter
  fieldKey: keyof Stage
  kpiType: string
  deviceTypeId?: string
  selectedAssetId?: string
  handleStageChange: (index: number, field: keyof Stage, value: Option | any, asset: Option | null, device: Option | null, constantValue?: string) => void
}

interface LabelValue {
  label: string
  value: string
  typeId?: string
}

const ParameterForm: React.FC<Props> = (props) => {
  const [selectedDevice, setSelectedDevice] = useState<LabelValue | null>(props.parameter.device !== '' ? { label: props.parameter.device, value: props.parameter.deviceId } : null)
  const [selectedParameter, setSelectedParameter] = useState<Option | null>(props.parameter.parameter !== '' ? { label: props.parameter.parameter, value: props.parameter.parameterId } : null)
  const [parameterOptions, setParameterOptions] = useState<Option[]>([])
  const [kpiParameterOptions, setKpiParameterOptions] = useState<Option[]>([])
  const [isConstant, setIsConstant] = useState(false)
  const [constantValue, setConstantValue] = useState<any>(props.parameter.constantValue !== undefined ? props.parameter.constantValue : '')
  const [constantErrorInit, setConstantErrorInit] = useState(false)
  const tempAssetVal = '00000000-0000-0000-0000-000000000000'

  useEffect(() => {
    if (props.isTotalizer) {
      setIsConstant(false)
      setConstantValue('')
      if (props.parameter?.parameterType === 'CONSTANT') {
        props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: 'CONSTANT' }, null, null, '')
        if (props.kpiType === 'option1') setSelectedDevice(null)
        setSelectedParameter(null)
      }
    }
  }, [props.isTotalizer])

  useEffect(() => {
    if (props.kpiType === 'option2' && props.deviceTypeId !== '' && props.deviceTypeId !== undefined) {
      props.fetchKpiList(0, 100, 'asc', 'kpiName', false) // to get all the kpi
      fetchData().catch(error => {
        console.error('Fetch configs failed:', error)
      })
    }
  }, [props.kpiType, props.deviceTypeId, props.isTotalizer])

  useEffect(() => {
    if (props.kpiType === 'option2' && props.deviceTypeId !== '' && props.deviceTypeId !== undefined) {
      if (props.parameter?.parameterType !== 'CONSTANT') {
        props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: '' }, null, null)
        setSelectedParameter(null)
      }
    }
  }, [props.deviceTypeId])

  useEffect(() => {
    if (props.kpiType === 'option1' && props.selectedAssetId !== '' && props.selectedAssetId !== undefined) {
      if (props.parameter?.parameterType !== 'CONSTANT') {
        props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: '' }, null, null)
        setSelectedDevice(null)
        setSelectedParameter(null)
      }
    }
  }, [props.selectedAssetId])

  const fetchData = async (): Promise<void> => {
    try {
      await fetchParameterConfigs(tempAssetVal, props.deviceTypeId ?? '', handleSuccess, handleFailure) // api to fetch parameter list in both level
      await getDeviceLevelKpiList(props.deviceTypeId ?? '', handleKpiSuccess, handleFailure) // api to get device level kpi list in device level
    } catch (error) {
      console.error('Failed to fetch parameters:', error)
    }
  }

  const fetchAssetData = async (deviceId: string, deviceTypeId: string): Promise<void> => {
    try {
      setParameterOptions([])
      await fetchParameterConfigs(deviceId, deviceTypeId, handleSuccess, handleFailure) // api to fetch parameter list in both level
      await fetchKpiConfigs(deviceId, deviceTypeId, handleKpiSuccess, handleFailure) // api to get asset level kpi list
    } catch (error) {
      console.error('Failed to fetch parameters:', error)
    }
  }

  const handleSuccess = (configs: ParameterConfig[], httpStatus: number): void => {
    let fetchedParameterOptions
    if (props.kpiType === 'option2') {
      fetchedParameterOptions = configs.map(param => ({
        label: `${param.parameterName?.toString() ?? ''}-${param.registerAddress?.toString() ?? ''}`,
        value: param.cimCodeId?.toString() ?? '',
        type: 'REGISTER_ID'
      }))
    } else {
      fetchedParameterOptions = configs
        .filter((param: { isThisFromDefaultConfig: boolean, isRegisterEnabled: boolean }) => !param.isThisFromDefaultConfig && param.isRegisterEnabled)
        .map(param => ({
          label: `${param.parameterName?.toString() ?? ''}-${param.registerAddress?.toString() ?? ''}`,
          value: param.cimCodeId?.toString() ?? '',
          type: 'REGISTER_ID'
        }))
    }
    const deviceParamOptions = props.kpiType === 'option2' ? [{ value: 'Constant', label: 'Constant', type: 'CONSTANT' }, ...fetchedParameterOptions] : fetchedParameterOptions
    const parameterOptions = props.isTotalizer ? fetchedParameterOptions : props.index === 0 ? deviceParamOptions : [{ value: '', label: 'None', type: '' }, ...deviceParamOptions]
    setParameterOptions(parameterOptions)
  }

  const handleKpiSuccess = (configs: any, httpStatus: number): void => {
    let fetchedKpiParameterOptions
    if (props.kpiType === 'option2') {
      fetchedKpiParameterOptions = configs.content
        .map((param: { kpi_name: { toString: () => string }, kpi_definition_id: { toString: () => string }, is_custom_kpi?: boolean }) => ({
          label: param.kpi_name?.toString() ?? '',
          value: param.kpi_definition_id?.toString() ?? '',
          type: (param.is_custom_kpi === true) ? 'CUSTOM_KPI' : 'STANDARD_KPI'
        }))
    } else {
      fetchedKpiParameterOptions = configs
        .filter((param: { isKpiEnabled: boolean, isCustomKpi: boolean, isDefaultConfig: boolean }) => param.isKpiEnabled && !param.isDefaultConfig)
        .map((param: { kpiName: { toString: () => string }, kpiDefinitionId: { toString: () => string }, isCustomKpi: boolean }) => ({
          label: param.kpiName?.toString() ?? '',
          value: param.kpiDefinitionId?.toString() ?? '',
          type: param.isCustomKpi ? 'CUSTOM_KPI' : 'STANDARD_KPI'
        }))
    }
    setKpiParameterOptions(fetchedKpiParameterOptions)
  }

  const handleFailure = (errorMessage: any, httpStatus: number): void => {
    console.error('Failed to fetch parameters:', errorMessage)
  }

  const handleDeviceChange = (value: LabelValue): void => {
    if (value.value === '' && props.index > 0) {
      setIsConstant(false)
      setSelectedDevice(null)
      setSelectedParameter(null)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '' }, null, null)
    } else if (value.value === 'Constant') {
      setIsConstant(true)
      setSelectedParameter(null)
      setSelectedDevice(value)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '' }, null, null, undefined)
    } else {
      setIsConstant(false)
      setConstantValue('')
      setSelectedParameter(null)
      setSelectedDevice(value)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: '' }, null, null)
      // const deviceTypeId = 'fdd42da4-4a13-4f57-9e1a-208d95c7f212'
      fetchAssetData(value.value, value.typeId ?? '').catch(error => {
        console.error('Fetch asset configs failed:', error)
      })
    }
  }

  const handleParameterChange = (value: Option): void => {
    if (value.value === '' && props.index > 0) {
      setIsConstant(false)
      setSelectedParameter(null)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '' }, null, null)
    } else if (value.value === 'Constant') {
      setIsConstant(true)
      setSelectedParameter(value)
      props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: 'CONSTANT' }, null, null, undefined)
    } else {
      setIsConstant(false)
      setSelectedParameter(value)
      props.handleStageChange(props.index, props.fieldKey, value, selectedDevice, selectedDevice)
    }
  }

  const fetchedDeviceOptions: LabelValue[] = (props.assetList != null)
    ? (props.assetList).map((device: { name: any, id: any, typeId: any }) => ({ label: device.name, value: device.id, typeId: device.typeId }))
    : []
  const deviceOptions = props.isTotalizer ? fetchedDeviceOptions : props.index === 0 ? [{ value: 'Constant', label: 'Constant' }, ...fetchedDeviceOptions] : [{ value: '', label: 'None' }, { value: 'Constant', label: 'Constant' }, ...fetchedDeviceOptions]

  const DEVICE_PLACEHOLDER = props.isTotalizer ? 'Select Device' : 'Select Device or Constant'
  const PARAMETER_PLACEHOLDER = 'Select Parameter'
  const CONST_PARAMETER_PLACEHOLDER = props.isTotalizer ? 'Select Parameter' : 'Select Parameter or Constant'

  const textStyle = {
    root: {
      backgroundColor: '#272727',
      '& .MuiInputBase-input': {
        color: '#F0F0F0',
        backgroundColor: '#404040'
      },
      '& .MuiInputLabel-root': {
        color: '#F0F0F0',
        '&.Mui-focused': {
          color: '#F0F0F0'
        }
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: '#00000000'
        },
        '&:hover fieldset': {
          borderColor: '#00000000'
        },
        '&.Mui-focused fieldset': {
          borderColor: '#00000000'
        }
      }
    }
  }

  const isValidConstant = (value: string): boolean => {
    const regexValidator = /^-?\d{1,500}(\.\d+)?$/
    return regexValidator.test(value)
  }
  const isConstantNotValid = (): boolean => {
    return (!isValidConstant(constantValue))
  }

  const handleConstantChange = (value: string): void => {
    setConstantValue(value)
    setConstantErrorInit(true)
    props.handleStageChange(props.index, props.fieldKey, { label: '', value: '', type: 'CONSTANT' }, null, null, value)
  }

  return (
      <div data-testid="parameter-form-input">
        <Grid container spacing={2}>
          {props.kpiType === 'option2'
            ? (
              <>
                <Grid item sx={{ marginTop: 1, marginBottom: 1 }}>
                  <span style={ commonTextStyle }>
                    <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>
                      { (props.fieldKey === 'parameter1' && props.isTotalizer) ? 'Condition Parameter to Totalize' : (props.fieldKey === 'parameter2' && props.isTotalizer) ? 'Variable to Totalize' : `${customKpi.Parameter} or ${customKpi.Constant}` }
                      {props.index === 0 ? <sup style={{ color: '#B0B0B0' }}> *</sup> : null}
                    </Typography>
                  </span>
                  <GenXDropdown
                    placeholder={CONST_PARAMETER_PLACEHOLDER}
                    options={[...parameterOptions, ...kpiParameterOptions]}
                    value={selectedParameter}
                    onSelect={handleParameterChange}
                    width={235}
                    inputId={`param-${props.fieldKey}-${props.index}`}
                  />
                </Grid>
                {isConstant && (
                  <Grid item>
                    <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '15px', padding: 0.5, display: 'block' }}>
                      {customKpi.Constant}
                      {props.index === 0
                        ? <sup style={{ color: '#B0B0B0' }}> *</sup>
                        : null
                      }
                    </Typography>
                    <TextField
                      id='remarks-textfield'
                      variant="outlined"
                      placeholder='Enter constant value'
                      InputLabelProps={{
                        disabled: true
                      }}
                      size='small'
                      className='custom-textfield'
                      value={constantValue}
                      onChange={(e) => { handleConstantChange(e.target.value) }}
                      sx={{
                        ...textStyle.root,
                        width: '112%',
                        border: (isConstantNotValid() && constantErrorInit) ? '1px solid #EE3426' : 'none'
                      }}
                    />
                    {(isConstantNotValid() && constantErrorInit) && (
                      <FormHelperText sx={{ color: '#EE3426', paddingLeft: '10px', whiteSpace: 'nowrap' }} className="errorColor">
                        {customKpi.ConstantValidationMessage}
                      </FormHelperText>
                    )}
                  </Grid>
                )}
              </>
              )
            : (
            <>
              <Grid item>
                <span style={ commonTextStyle }>
                  <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>
                    { (props.isTotalizer) ? 'Asset or Device' : `Asset or ${customKpi.Device} or ${customKpi.Constant}` }
                    {props.index === 0 ? <sup style={{ color: '#B0B0B0' }}> *</sup> : null}
                  </Typography>
                </span>
                <GenXDropdown
                  placeholder={DEVICE_PLACEHOLDER}
                  options={deviceOptions}
                  value={selectedDevice}
                  onSelect={handleDeviceChange}
                  width={235}
                  inputId={`device-${props.fieldKey}-${props.index}`}
                />
              </Grid>
              {!isConstant
                ? (
                    <Grid item>
                      <span style={ commonTextStyle }>
                        <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '14px', letterSpacing: '0.5px' }}>
                          { (props.fieldKey === 'parameter1' && props.isTotalizer) ? 'Condition Parameter to Totalize' : (props.fieldKey === 'parameter2' && props.isTotalizer) ? 'Variable to Totalize' : props.fieldKey === 'parameter1' ? 'Parameter 1' : 'Parameter 2' }
                          {props.index === 0 ? <sup style={{ color: '#B0B0B0' }}> *</sup> : null}
                        </Typography>
                      </span>
                        <GenXDropdown
                          placeholder={PARAMETER_PLACEHOLDER}
                          options={[...parameterOptions, ...kpiParameterOptions]}
                          value={selectedParameter}
                          onSelect={handleParameterChange}
                          width={235}
                          inputId={`param-${props.fieldKey}-${props.index}`}
                        />
                    </Grid>
                  )
                : (
                    <Grid item>
                      <Typography sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '15px', padding: 0.5, display: 'block' }}>
                        {customKpi.Constant}
                        {props.index === 0
                          ? <sup style={{ color: '#B0B0B0' }}> *</sup>
                          : null
                        }
                      </Typography>
                      <TextField
                        id='remarks-textfield'
                        variant="outlined"
                        placeholder='Enter constant value'
                        InputLabelProps={{
                          disabled: true
                        }}
                        size='small'
                        className='custom-textfield'
                        value={constantValue}
                        onChange={(e) => { handleConstantChange(e.target.value) }}
                        sx={{
                          ...textStyle.root,
                          width: '112%',
                          border: (isConstantNotValid() && constantErrorInit) ? '1px solid #EE3426' : 'none'
                        }}
                      />
                      {(isConstantNotValid() && constantErrorInit) && (
                        <FormHelperText sx={{ color: '#EE3426', paddingLeft: '10px', whiteSpace: 'nowrap' }} className="errorColor">
                          {customKpi.ConstantValidationMessage}
                        </FormHelperText>
                      )}
                    </Grid>
                  )
              }
            </>
              )}
        </Grid>
      </div>
  )
}

interface DispatchToProps {
  fetchKpiList: (page: number, size: number, sortdir: string, sortfield: string, filter: boolean) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchKpiList: (page: number, size: number, sortdir: string, sortfield: string, filter: boolean) => dispatch(fetchKpiList(page, size, sortdir, sortfield, filter))
})

interface StateToProps {
  deviceParameterList: ParameterConfigState
  kpiList: KpiListState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  deviceParameterList: state.deviceParameters,
  kpiList: state.kpiList
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type ReduxProps = ConnectedProps<typeof connector>
type Props = ParameterFormProps & ReduxProps

export default connector(ParameterForm)
